import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, fileUrl, formatApiError } from "../lib/api";
import FieldRenderer from "../components/app/FieldRenderer";
import { Skull, Check, ChevronRight, ChevronLeft, Loader2, Lock } from "lucide-react";

export default function PublicApply() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [appsOpen, setAppsOpen] = useState(true);
  const [closedMsg, setClosedMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showDates, setShowDates] = useState([]);
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [filenames, setFilenames] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const formUrl = slug ? `${API}/public/form/${slug}` : `${API}/public/form`;
    axios.get(formUrl).then(({ data: res }) => {
      const f = res.form;
      setForm(f);
      setEventInfo(res.event);
      setAppsOpen(res.applications_open);
      setClosedMsg(res.closed_message || "");
      // Initialize defaults
      const init = {};
      (f?.fields || []).forEach((field) => {
        if (field.type === "text_block") return; // skip — no input
        if (field.type === "availability_calendar") init[field.id] = [];
        else if (field.type === "yes_no" || field.type === "checkbox") init[field.id] = null;
        else init[field.id] = "";
      });
      setData(init);
    }).catch((e) => {
      if (e?.response?.status === 404) setNotFound(true);
      else setError(formatApiError(e));
    }).finally(() => setLoading(false));
    const sdUrl = slug ? `${API}/public/show-dates?slug=${slug}` : `${API}/public/show-dates`;
    axios.get(sdUrl).then(({ data }) => setShowDates(data.show_dates || []));
  }, [slug]);

  // Group fields by section
  const sections = useMemo(() => {
    if (!form) return [];
    const order = form.sections && form.sections.length > 0 ? form.sections : ["General"];
    const grouped = order.map((name) => ({ name, fields: [] }));
    (form.fields || []).forEach((f) => {
      const sectionName = f.section || order[0];
      let sec = grouped.find((g) => g.name === sectionName);
      if (!sec) { sec = { name: sectionName, fields: [] }; grouped.push(sec); }
      sec.fields.push(f);
    });
    return grouped.filter((s) => s.fields.length > 0);
  }, [form]);

  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleFileUpload = async (fieldId, file) => {
    setUploadingField(fieldId); setError(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: r } = await axios.post(`${API}/public/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(fieldId, r.file_id);
      setFilenames((fn) => ({ ...fn, [fieldId]: file.name }));
    } catch (err) { setError(formatApiError(err)); }
    finally { setUploadingField(null); }
  };

  const isFieldFilled = (f) => {
    const v = data[f.id];
    if (f.type === "text_block") return true; // never required
    if (f.type === "availability_calendar") return Array.isArray(v) && v.length > 0;
    if (f.type === "yes_no" || f.type === "checkbox") return v === true || v === false;
    if (f.type === "number") return v !== null && v !== undefined && v !== "";
    return v !== null && v !== undefined && String(v).trim() !== "";
  };

  const validateSection = (idx) => {
    const sec = sections[idx];
    if (!sec) return null;
    for (const f of sec.fields) {
      if (f.required && !isFieldFilled(f)) {
        return `${f.label} is required`;
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateSection(step);
    if (err) { setError(err); return; }
    setError(null);
    setStep((s) => Math.min(sections.length - 1, s + 1));
  };

  const submit = async () => {
    const err = validateSection(step);
    if (err) { setError(err); return; }
    // Map field ids to backend submission. Ensure full_name, email, gdpr_consent are set.
    const payload = { ...data };
    // Find gdpr field if labeled differently
    const gdprField = (form.fields || []).find((f) => f.id === "gdpr_consent" || f.type === "checkbox");
    if (gdprField && payload[gdprField.id] !== undefined) payload.gdpr_consent = !!payload[gdprField.id];
    // Derive full_name from first_name + last_name (Update 09 split). Falls back to any legacy full_name field.
    if (!payload.full_name) {
      const derived = [payload.first_name, payload.last_name].filter(Boolean).join(" ").trim();
      if (derived) payload.full_name = derived;
    }
    if (!payload.full_name || !payload.email) { setError("Name and Email are required system fields."); return; }
    if (!payload.gdpr_consent) { setError("GDPR consent is required."); return; }
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/public/applications`, { ...payload, event_id: eventInfo?.id });
      setSubmitted(true);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1530374195309-8c25022c4753?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600)", backgroundSize: "cover" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/95" />
        <div className="relative max-w-xl text-center animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 border border-blood mb-8"><Check size={36} className="text-blood" strokeWidth={1.5} /></div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mb-4 text-[#F2EBE1]" data-testid="apply-success-title">Application Received</h1>
          <p className="text-[#A19D94] text-lg leading-relaxed">
            Thank you for applying to <span className="text-blood">Stourbridge Scare Maze</span>.
            A confirmation has been sent to your email. We&apos;ll be in touch about next steps.
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-[#A19D94]"><Loader2 className="animate-spin" /></div>;

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1530374195309-8c25022c4753?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600)", backgroundSize: "cover" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/95" />
        <div className="relative max-w-xl text-center animate-fade-in" data-testid="apply-not-found">
          <div className="inline-flex items-center gap-3 mb-6"><Skull size={30} className="text-blood" strokeWidth={1.5} /><span className="font-display text-2xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span></div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4 text-[#F2EBE1]">Event Not Found</h1>
          <p className="text-[#A19D94] text-lg leading-relaxed">We couldn&apos;t find an application for this link. Please double-check the URL or contact the organisers.</p>
        </div>
      </div>
    );
  }

  if (!appsOpen) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1530374195309-8c25022c4753?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600)", backgroundSize: "cover" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/95" />
        <div className="relative max-w-xl text-center animate-fade-in" data-testid="apply-closed">
          <div className="inline-flex items-center justify-center w-20 h-20 border border-blood mb-8"><Lock size={32} className="text-blood" strokeWidth={1.5} /></div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-4 text-[#F2EBE1]">Applications Closed</h1>
          <p className="text-[#A19D94] text-lg leading-relaxed">{closedMsg || "Applications for this event are now closed. Thank you for your interest."}</p>
          {eventInfo?.name && <p className="text-xs text-[#4a4842] tracking-wider uppercase mt-8">{eventInfo.name}</p>}
        </div>
      </div>
    );
  }

  if (!form) return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-[#A19D94]">No application form configured for this event.</div>;

  const multiStep = form.layout === "multi-step";
  const showAllAtOnce = !multiStep;
  const currentSection = sections[step];

  const bgImg = form.background_image_file_id ? fileUrl(form.background_image_file_id) : null;
  const bgColor = form.background_color || "#050505";
  const containerStyle = bgImg
    ? { backgroundColor: bgColor, backgroundImage: `url(${bgImg})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat", backgroundAttachment: "fixed" }
    : { backgroundColor: bgColor };

  // Show the dark horror default overlay only when no custom background image is set
  const useDefaultOverlay = !bgImg && (!form.background_color || form.background_color === "#050505");

  return (
    <div className="min-h-screen text-[#F2EBE1] relative" style={containerStyle}>
      {useDefaultOverlay && (
        <>
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1530374195309-8c25022c4753?crop=entropy&cs=srgb&fm=jpg&q=70&w=1600)", backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black pointer-events-none" />
        </>
      )}
      {bgImg && <div className="absolute inset-0 bg-black/40 pointer-events-none" />}

      <div className="relative max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          {form.logo_file_id ? (
            <div className="flex justify-center mb-6" data-testid="form-logo">
              <img
                src={fileUrl(form.logo_file_id)}
                alt="logo"
                className="max-h-28 max-w-[260px] object-contain"
              />
            </div>
          ) : (
            <div className="inline-flex items-center gap-3 mb-4">
              <Skull size={32} className="text-blood" strokeWidth={1.5} />
              <span className="font-display text-3xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span>
            </div>
          )}
          {(form.title || form.name) && (
            <h1 data-testid="form-title" className="font-display text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-none mb-3">
              {form.title || form.name}
            </h1>
          )}
          {form.subtext && (
            <p data-testid="form-subtext" className="text-[#A19D94] tracking-wide whitespace-pre-wrap">{form.subtext}</p>
          )}
        </div>

        {multiStep && sections.length > 1 && (
          <div className="flex items-center justify-center gap-2 mb-10 flex-wrap" data-testid="apply-stepper">
            {sections.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2">
                <button onClick={() => i < step && setStep(i)} className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                  i === step ? "border-blood text-blood" : i < step ? "border-[#262626] text-[#A19D94]" : "border-[#1a1a1a] text-[#4a4842]"
                }`}>
                  <span className="font-mono mr-2">{String(i+1).padStart(2,'0')}</span>{s.name}
                </button>
                {i < sections.length - 1 && <ChevronRight size={14} className="text-[#3a3835]" />}
              </div>
            ))}
          </div>
        )}

        <div className="border border-[#1a1a1a] bg-[#0A0A0A]/90 backdrop-blur-sm p-8 md:p-10">
          {error && <div className="mb-6 p-3 border border-blood/40 bg-blood/10 text-sm text-blood" data-testid="apply-error">{error}</div>}

          {(showAllAtOnce ? sections : [currentSection]).filter(Boolean).map((sec, idx) => (
            <div key={sec.name} className={idx > 0 ? "mt-10 pt-10 border-t border-[#1a1a1a]" : ""}>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-mono text-xs text-blood">{String((showAllAtOnce ? idx : step) + 1).padStart(2,'0')}</span>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{sec.name}</h2>
              </div>
              <div className="space-y-5">
                {sec.fields.map((f) => (
                  <div key={f.id}>
                    {f.type !== "checkbox" && f.type !== "text_block" && (
                      <label className="label-mini block mb-1.5">{f.label}{f.required && " *"}</label>
                    )}
                    <FieldRenderer
                      field={f}
                      value={data[f.id]}
                      onChange={(v) => upd(f.id, v)}
                      showDates={showDates}
                      onFileUpload={(file) => handleFileUpload(f.id, file)}
                      uploading={uploadingField === f.id}
                      filename={filenames[f.id]}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between pt-8 mt-8 border-t border-[#1a1a1a]">
            {multiStep ? (
              <>
                <button type="button" onClick={() => setStep((s) => Math.max(0, s-1))} disabled={step === 0} data-testid="apply-prev-btn"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm border border-[#262626] text-[#A19D94] hover:border-[#3a3835] disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronLeft size={14} /> Back
                </button>
                {step < sections.length - 1 ? (
                  <button type="button" onClick={goNext} data-testid="apply-next-btn"
                    className="flex items-center gap-2 px-6 py-2.5 text-sm font-display tracking-wider uppercase bg-blood text-white hover:bg-[#FF3333] transition-colors">
                    Continue <ChevronRight size={14} />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={submitting} data-testid="apply-submit-btn"
                    className="flex items-center gap-2 px-6 py-2.5 text-sm font-display tracking-wider uppercase bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting</> : <>Submit Application <ChevronRight size={14} /></>}
                  </button>
                )}
              </>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} data-testid="apply-submit-btn"
                className="ml-auto flex items-center gap-2 px-6 py-2.5 text-sm font-display tracking-wider uppercase bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting</> : <>Submit Application <ChevronRight size={14} /></>}
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 text-center">
          <span className="text-xs text-[#3a3835] tracking-wider uppercase">© {new Date().getFullYear()} Stourbridge Scare Maze</span>
        </div>
      </div>
    </div>
  );
}
